import { usePrivy, useWallets } from '@privy-io/react-auth';
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { useSetActiveWallet } from '@privy-io/wagmi';

interface AppContextType {
  toggleBridgeModal: boolean;
  setToggleBridgeModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toggleBridgeModal, setToggleBridgeModal] = useState(false);
  const { authenticated, ready, user } = usePrivy();
  const { wallets, ready: walletReady } = useWallets();
  const isWalletReady = useRef<boolean>(false);
  const { setActiveWallet } = useSetActiveWallet();

  const checkIsWalletReady = () => {
    setTimeout(() => {
      if (
        !isWalletReady.current &&
        user?.wallet?.connectorType !== 'embedded'
      ) {
        window.location.reload();
      }
    }, 5000);
  };

  useEffect(() => {
    if (authenticated && ready && user?.wallet && wallets.length > 0) {
      checkIsWalletReady();

      let userWallet = wallets.find(
        (w) => w.address.toLowerCase() === user?.wallet?.address.toLowerCase()
      );

      if (userWallet) {
        setActiveWallet(userWallet);
      }
    }
  }, [authenticated, ready, user, wallets]);

  useEffect(() => {
    isWalletReady.current = walletReady;
  }, [walletReady]);

  return (
    <AppContext.Provider value={{ toggleBridgeModal, setToggleBridgeModal }}>
      {children}
    </AppContext.Provider>
  );
};

// Share common state across pages
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
